<template>
  <div class="flex flex-col gap-4">
    <div class="overflow-x-auto rounded-lg border">
      <UmisisDataTable
        :loading="isLoading"
        :order-by="orderBy"
        :order-direction="orderDirection"
        :fields="fields"
        :rows="getItems"
        @sort-changed="handleSort"
      />
    </div>

    <Pagination
      v-model:page="taskTemplateStore.page"
      :items-per-page="taskTemplateStore.perPage"
      :total="taskTemplateStore.total ?? 0"
    />
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted, watch } from 'vue'
import UmisisDataTable from '../../../../../../../resources/js/components/umisis/generic/UmisisDataTable.vue'
import { Pagination } from '../../../../../../../resources/js/components/ui/pagination/index.js'
import TaskTemplateAssigneeField from './fields/TaskTemplateAssigneeField.vue'
import TaskTemplateDefaultDeadlineField from './fields/TaskTemplateDefaultDeadlineField.vue'
import TaskTemplateLaunchField from './fields/TaskTemplateLaunchField.vue'
import TaskTemplatePriorityField from './fields/TaskTemplatePriorityField.vue'
import TaskTemplateTitleField from './fields/TaskTemplateTitleField.vue'
import { useTaskTemplateStore } from '../../stores/taskTemplateStore.js'

const props = defineProps({
  context: {
    type: String,
    default: 'task-templates-list',
  },
})

const taskTemplateStore = useTaskTemplateStore(props.context)
const { getItems, isLoading, orderBy, orderDirection, page, perPage } =
  storeToRefs(taskTemplateStore)

const fields = computed(() => {
  return [
    { name: 'id', label: 'ID', sortable: true },
    {
      name: 'title',
      label: 'Title',
      sortable: true,
      searchable: true,
      component: TaskTemplateTitleField,
      resolveProps: (row) => ({
        taskTemplate: row,
        context: props.context,
      }),
    },
    {
      name: 'priority',
      label: 'Priority',
      sortable: true,
      component: TaskTemplatePriorityField,
      resolveProps: (row) => ({
        taskTemplate: row,
        context: props.context,
      }),
    },
    {
      name: 'assigned_to_user_id',
      label: 'Assigned To',
      sortable: true,
      component: TaskTemplateAssigneeField,
      resolveProps: (row) => ({
        taskTemplate: row,
        context: props.context,
      }),
    },
    {
      name: 'created_by_user_id',
      label: 'Created By',
      sortable: true,
      formatter: (value, row) => {
        return row?.cached?.created_by_user?.name ?? (value ? `User #${value}` : '-')
      },
    },
    {
      name: 'default_deadline_after_minutes',
      label: 'Default Deadline',
      sortable: true,
      component: TaskTemplateDefaultDeadlineField,
      resolveProps: (row) => ({
        taskTemplate: row,
        context: props.context,
      }),
    },
    {
      name: 'created_at',
      label: 'Created At',
      sortable: true,
      formatter: (value) => {
        if (!value) {
          return '-'
        }

        return new Date(value).toLocaleDateString()
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      component: TaskTemplateLaunchField,
      resolveProps: (row) => ({
        taskTemplate: row,
        context: props.context,
      }),
    },
  ]
})

const fetchTaskTemplates = async () => {
  await taskTemplateStore.fetchItems({
    clear: true,
    page: taskTemplateStore.page,
    per_page: taskTemplateStore.perPage,
    order_by: taskTemplateStore.orderBy,
    order_direction: taskTemplateStore.orderDirection,
  })
}

const handleSort = (params) => {
  taskTemplateStore.sortChanged(params)
}

watch([page, perPage, orderBy, orderDirection], async () => {
  await fetchTaskTemplates()
})

onMounted(async () => {
  await fetchTaskTemplates()
})
</script>
